import { colors } from "../../../theme/theme";
import { STATUS_CHIP_STYLES } from "./Patient.constants";

export const getPractitionerName = (assignedUserId, practitioners) => {
  if (!assignedUserId || !practitioners || practitioners.length === 0) {
    return "N/A";
  }
  const practitioner = practitioners.find((p) => p.userId === assignedUserId);
  return practitioner ? practitioner.name : "N/A";
};

export const getPatientEmail = (appointment, patient) => {
  return appointment.contact?.email || patient?.contact?.email || null;
};

export const isFirstAppointmentForPatient = (
  appointments,
  currentAppointment
) => {
  if (!appointments || appointments.length === 0) return false;

  // Filter out cancelled appointments (keep rescheduled) so previous
  // cancelled/test bookings don't steal the "first appointment" slot.
  const activeAppointments = appointments.filter((appt) => {
    const status = appt.appointmentStatus?.toLowerCase();
    if (status !== "cancelled") return true;
    return appt.notes?.toLowerCase().includes("rescheduled");
  });

  const relevant =
    activeAppointments.length > 0 ? activeAppointments : appointments;

  const appointmentsWithDate = relevant.filter((appt) => appt.dateAdded);

  if (appointmentsWithDate.length === 0) {
    const sortedByStartTime = [...relevant].sort(
      (a, b) => new Date(a.startTime) - new Date(b.startTime)
    );
    return currentAppointment.id === sortedByStartTime[0].id;
  }

  const sortedByBookingDate = [...appointmentsWithDate].sort(
    (a, b) => new Date(a.dateAdded) - new Date(b.dateAdded)
  );
  const firstBookedAppointment = sortedByBookingDate[0];

  return currentAppointment.id === firstBookedAppointment.id;
};

/**
 * Checks if the client has completed their first consultation.
 * The first appointment (by booking date) must have status "showed" or "completed".
 * Returns false if there are no appointments or the first one isn't completed yet.
 *
 * Cancelled (non-rescheduled) appointments are excluded so that old test
 * bookings don't permanently block the patient from scheduling follow-ups.
 */
export const hasCompletedConsultation = (appointments) => {
  if (!appointments || appointments.length === 0) return false;

  // Filter out cancelled appointments (keep rescheduled)
  const activeAppointments = appointments.filter((appt) => {
    const status = appt.appointmentStatus?.toLowerCase();
    if (status !== "cancelled") return true;
    return appt.notes?.toLowerCase().includes("rescheduled");
  });

  const relevant =
    activeAppointments.length > 0 ? activeAppointments : appointments;

  const appointmentsWithDate = relevant.filter((appt) => appt.dateAdded);

  let firstAppointment;
  if (appointmentsWithDate.length > 0) {
    const sortedByBookingDate = [...appointmentsWithDate].sort(
      (a, b) => new Date(a.dateAdded) - new Date(b.dateAdded)
    );
    firstAppointment = sortedByBookingDate[0];
  } else {
    const sortedByStartTime = [...relevant].sort(
      (a, b) => new Date(a.startTime) - new Date(b.startTime)
    );
    firstAppointment = sortedByStartTime[0];
  }

  const status = firstAppointment.appointmentStatus?.toLowerCase();
  return status === "showed" || status === "completed";
};

export const filterValidAppointments = (appointmentsList) => {
  return appointmentsList.filter(
    (appt) =>
      appt.appointmentStatus &&
      appt.appointmentStatus.toLowerCase() !== "invalid" &&
      appt.appointmentStatus.toLowerCase() !== "deleted" &&
      appt.id &&
      appt.startTime
  );
};

export const isAppointmentPaid = (appointmentId, payments) => {
  if (!payments || payments.length === 0) return false;

  const payment = payments.find(
    (p) =>
      p.appointmentId === appointmentId &&
      (p.status === "completed" || p.status === "paid")
  );

  return !!payment;
};

export const isComplimentary = (appointments, currentAppointment, patient) => {
  const patientEmail = getPatientEmail(currentAppointment, patient);

  if (!patientEmail) return false;

  const patientAppointments = appointments.filter(
    (appt) => appt.contact?.email === patientEmail
  );

  if (patientAppointments.length === 0) return false;

  // Filter out cancelled appointments (but keep rescheduled ones) so that
  // previous cancelled/test bookings don't prevent the real first session
  // from being recognised as the complimentary consultation.
  const activeAppointments = patientAppointments.filter((appt) => {
    const status = appt.appointmentStatus?.toLowerCase();
    if (status !== "cancelled") return true;
    // Keep cancelled appointments that are actually rescheduled
    return appt.notes?.toLowerCase().includes("rescheduled");
  });

  // Use active appointments when available; fall back to all appointments
  // so we never accidentally return false for every appointment.
  const relevantAppointments =
    activeAppointments.length > 0 ? activeAppointments : patientAppointments;

  const appointmentsWithDate = relevantAppointments.filter(
    (appt) => appt.dateAdded
  );

  if (appointmentsWithDate.length > 0) {
    const sortedByBookingDate = [...appointmentsWithDate].sort(
      (a, b) => new Date(a.dateAdded) - new Date(b.dateAdded)
    );
    const firstBookedAppointment = sortedByBookingDate[0];
    return currentAppointment.id === firstBookedAppointment.id;
  }

  const sortedByStartTime = [...relevantAppointments].sort(
    (a, b) => new Date(a.startTime) - new Date(b.startTime)
  );
  const firstAppointment = sortedByStartTime[0];

  return currentAppointment.id === firstAppointment.id;
};

export const needsPayment = (
  appointments,
  currentAppointment,
  payments,
  patient
) => {
  const patientEmail = getPatientEmail(currentAppointment, patient);

  if (!patientEmail) return false;
  if (isAppointmentPaid(currentAppointment.id, payments)) return false;
  if (isComplimentary(appointments, currentAppointment, patient)) return false;

  return true;
};

export const getStatusChipProps = (status, notes, isUpcomingSession) => {
  const statusLower = status?.toLowerCase() || "";
  if (!isUpcomingSession) {
    return {
      label: status,
      sx: {
        ...STATUS_CHIP_STYLES.past,
        ...STATUS_CHIP_STYLES.base,
      },
    };
  }
  // Detect rescheduled appointments (cancelled with "Rescheduled" in notes)
  if (
    statusLower === "cancelled" &&
    notes &&
    notes.toLowerCase().includes("rescheduled")
  ) {
    return {
      label: "Rescheduled",
      sx: { ...STATUS_CHIP_STYLES.base, ...STATUS_CHIP_STYLES.rescheduled },
    };
  }

  switch (statusLower) {
    case "confirmed":
      return {
        label: "Confirmed",
        sx: { ...STATUS_CHIP_STYLES.base, ...STATUS_CHIP_STYLES.confirmed },
      };
    case "cancelled":
      return {
        label: "Cancelled",
        sx: { ...STATUS_CHIP_STYLES.base, ...STATUS_CHIP_STYLES.cancelled },
      };
    case "new":
    case "pending":
      return {
        label: "Pending",
        sx: { ...STATUS_CHIP_STYLES.base, ...STATUS_CHIP_STYLES.pending },
      };
    case "showed":
    case "completed":
      return {
        label: "Completed",
        sx: { ...STATUS_CHIP_STYLES.base, ...STATUS_CHIP_STYLES.completed },
      };
    case "noshow":
    case "no_show":
      return {
        label: "No Show",
        sx: { ...STATUS_CHIP_STYLES.base, ...STATUS_CHIP_STYLES.noshow },
      };
    default:
      return {
        label: status,
        sx: {
          ...STATUS_CHIP_STYLES.base,
          border: `1px solid ${colors.primary}`,
          color: colors.primary,
        },
      };
  }
};

export const buildCalendarBookingUrl = (
  practitioner,
  patient,
  appointment
) => {
  const calendarId =
    practitioner.primaryCalendar?.id || practitioner.calendars?.[0]?.id;
  if (!calendarId) return null;

  const patientEmail = patient?.contact?.email || appointment.contact?.email;
  const patientName =
    patient?.contact?.name || appointment.contact?.name || appointment.title;
  const patientPhone = patient?.contact?.phone || appointment.contact?.phone;

  const baseUrl = `https://api.leadconnectorhq.com/widget/booking/${calendarId}`;
  const params = new URLSearchParams();

  if (patientName) {
    const nameParts = patientName.split(" ");
    if (nameParts.length > 0) params.append("first_name", nameParts[0]);
    if (nameParts.length > 1)
      params.append("last_name", nameParts.slice(1).join(" "));
  }
  if (patientEmail) params.append("email", patientEmail);
  if (patientPhone) params.append("phone", patientPhone);

  return `${baseUrl}?${params.toString()}`;
};

export const calculateAppointmentCharge = (startTime, endTime, hourlyRate) => {
  const start = new Date(startTime);
  const end = new Date(endTime);

  const durationInMinutes = Math.round((end - start) / (1000 * 60));
  const chargeAmount = (durationInMinutes / 60) * hourlyRate;

  return {
    durationInMinutes,
    chargeAmount: parseFloat(chargeAmount.toFixed(2)),
  };
};
